import React, { useState } from "react";
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  Callout,
} from "@appsmith/ads";
import { useDispatch, useSelector } from "react-redux";
import { deleteAccount } from "actions/AccountDeletionActions";
import { AppState } from "reducers";
import {
  createMessage,
  ACCOUNT_DELETE_MODAL_HEADER,
  ACCOUNT_DELETE_MODAL_BODY,
  ACCOUNT_DELETE_MODAL_CONFIRM_LABEL,
  ACCOUNT_DELETE_MODAL_CONFIRM,
} from "constants/Messages";

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeleteAccountModal = ({ isOpen, onClose }: DeleteAccountModalProps) => {
  const dispatch = useDispatch();
  const [confirmText, setConfirmText] = useState("");
  const isLoading = useSelector(
    (state: AppState) => state.accountDeletion.deleteLoading,
  );
  const preflight = useSelector(
    (state: AppState) => state.accountDeletion.preflight.data,
  );

  const isConfirmed = confirmText === "DELETE";

  const handleConfirm = () => {
    if (isConfirmed) {
      dispatch(deleteAccount("User-initiated account deletion"));
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setConfirmText("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Modal onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <ModalContent className="!w-[560px]">
        <ModalHeader>{createMessage(ACCOUNT_DELETE_MODAL_HEADER)}</ModalHeader>
        <ModalBody>
          <Flex flexDirection="column" gap="spaces-4">
            <Text kind="body-m">
              {createMessage(ACCOUNT_DELETE_MODAL_BODY)}
            </Text>
            {preflight?.warnings?.map((warning: string) => (
              <Callout key={warning} kind="warning">
                {warning}
              </Callout>
            ))}
            {preflight?.summary && (
              <Flex
                bg="var(--ads-v2-color-bg-subtle)"
                borderRadius="var(--ads-v2-border-radius)"
                flexDirection="column"
                gap="spaces-2"
                padding="spaces-3"
              >
                <Text kind="body-s">Records to be deleted:</Text>
                {preflight.summary.licenseFound && (
                  <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                    {`• 1 license (${
                      preflight.summary.licenseStatus ?? "UNKNOWN"
                    })`}
                  </Text>
                )}
                {preflight.summary.subscriptionFound && (
                  <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                    {"• 1 subscription"}
                  </Text>
                )}
                {preflight.summary.memberCount > 0 && (
                  <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                    {`• ${preflight.summary.memberCount} member(s)`}
                  </Text>
                )}
                {preflight.summary.usagePulseCount > 0 && (
                  <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                    {`• ${preflight.summary.usagePulseCount} usage record(s)`}
                  </Text>
                )}
              </Flex>
            )}
            <Flex flexDirection="column" gap="spaces-2">
              <Text kind="body-s">
                {createMessage(ACCOUNT_DELETE_MODAL_CONFIRM_LABEL)}
              </Text>
              <input
                autoComplete="off"
                className="px-3 py-2 border rounded text-sm"
                disabled={isLoading}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{
                  border: "1px solid var(--ads-v2-color-border)",
                  borderRadius: "var(--ads-v2-border-radius)",
                  padding: "8px 12px",
                  fontSize: "14px",
                  width: "100%",
                }}
                value={confirmText}
              />
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex gap="spaces-3" justifyContent="flex-end">
            <Button
              isDisabled={isLoading}
              kind="secondary"
              onClick={handleClose}
              size="md"
            >
              Cancel
            </Button>
            <Button
              isDisabled={!isConfirmed}
              isLoading={isLoading}
              kind="error"
              onClick={handleConfirm}
              size="md"
            >
              {createMessage(ACCOUNT_DELETE_MODAL_CONFIRM)}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { DeleteAccountModal };
